// Item comparison (Phaser-free): simulates the equip on a cloned loadout and
// reports what actually changes — stat deltas (synergy bonuses included), a
// DPS estimate for weapons and mods, weapon-mod modifier changes, tags and
// synergies gained or lost.

import type { ItemComparison, ItemInstance, StatBlock, StatDelta, StatKey, Tag, WeaponInstance } from '../../core/types';
import {
  applyEquip,
  canAutoEquip,
  cloneItem,
  cloneLoadout,
  modTargetSlot,
  replacedBy,
} from './loadout';
import {
  STAT_INFO,
  STAT_KEYS,
  activeSynergiesFor,
  combineModifiers,
  computeStatsFor,
  estimateDps,
  itemTags,
  signed,
  type CombinedModifier,
  type Loadout,
} from './stats';

/** Formatted delta for one stat, or null when the change is negligible. */
export function statDelta(stat: StatKey, before: number, after: number): StatDelta | null {
  const info = STAT_INFO[stat];
  let d: number;
  let value: string;
  switch (info.mode) {
    case 'flat':
      d = after - before;
      if (Math.abs(d) < 0.5) return null;
      value = signed(d);
      break;
    case 'pts':
      d = (after - before) * 100;
      if (Math.abs(d) < 0.5) return null;
      value = `${signed(d)}%`;
      break;
    case 'count':
      d = Math.round(after - before);
      if (d === 0) return null;
      value = signed(d);
      break;
    case 'sec':
      d = after - before;
      if (Math.abs(d) < 0.05) return null;
      value = `${signed(d, 1)}s`;
      break;
    default: {
      // Relative change of a multiplier / rate.
      d = before > 1e-6 ? after / before - 1 : after - before;
      if (Math.abs(d) < 0.005) return null;
      value = `${signed(d * 100)}%`;
    }
  }
  return { label: info.label, value, better: info.lowerIsBetter ? d < 0 : d > 0 };
}

function relDelta(label: string, before: number, after: number, lowerIsBetter = false): StatDelta | null {
  if (before <= 1e-6) return null;
  const d = after / before - 1;
  if (Math.abs(d) < 0.005) return null;
  return { label, value: `${signed(d * 100)}%`, better: lowerIsBetter ? d < 0 : d > 0 };
}

function dpsDelta(before: number | null, after: number): StatDelta {
  const b = Math.round(after);
  if (before === null) return { label: 'Est. DPS', value: `${b}`, better: null };
  const a = Math.round(before);
  const better = after > before * 1.03 ? true : after < before * 0.97 ? false : null;
  return { label: 'Est. DPS', value: `${a} → ${b}`, better };
}

const STATUS_LABEL: Record<string, string> = {
  burn: 'Burn',
  shock: 'Shock',
  slow: 'Slow',
  vulnerable: 'Vulnerable',
  stun: 'Stun',
  overheated: 'Overheat',
};

/** Weapon-local changes from installing / swapping a mod. */
function modifierDeltas(before: CombinedModifier, after: CombinedModifier): StatDelta[] {
  const out: StatDelta[] = [];
  const push = (d: StatDelta | null) => d && out.push(d);
  push(relDelta('Damage', before.damageMult, after.damageMult));
  push(relDelta('Fire rate', before.fireRateMult, after.fireRateMult));
  push(relDelta('Heat per shot', before.heatMult, after.heatMult, true));
  push(relDelta('Cooling', before.coolMult, after.coolMult));
  push(relDelta('Projectile speed', before.projectileSpeedMult, after.projectileSpeedMult));
  push(relDelta('Knockback', before.knockbackMult, after.knockbackMult));
  const proj = after.extraProjectiles - before.extraProjectiles;
  if (proj !== 0) out.push({ label: 'Projectiles', value: signed(proj), better: proj > 0 });
  const pierce = after.pierceAdd - before.pierceAdd;
  if (pierce !== 0) out.push({ label: 'Pierce', value: signed(pierce), better: pierce > 0 });
  const burstB = before.burst?.count ?? 1;
  const burstA = after.burst?.count ?? 1;
  if (burstA !== burstB) {
    out.push({ label: 'Fire mode', value: burstA > 1 ? `${burstA}-round burst` : 'Single shot', better: null });
  }
  const statusB = new Set(before.addStatuses.map((s) => s.id));
  const statusA = new Set(after.addStatuses.map((s) => s.id));
  for (const id of statusA) if (!statusB.has(id)) out.push({ label: 'On hit', value: STATUS_LABEL[id] ?? id, better: true });
  for (const id of statusB) if (!statusA.has(id)) out.push({ label: 'On hit', value: `No ${(STATUS_LABEL[id] ?? id).toLowerCase()}`, better: false });
  if (!!after.explodeOnKill !== !!before.explodeOnKill) {
    out.push({ label: 'Kills explode', value: after.explodeOnKill ? 'Yes' : 'No', better: !!after.explodeOnKill });
  }
  return out;
}

const ORDER = (d: StatDelta) => (d.better === true ? 0 : d.better === null ? 1 : 2);

/**
 * Compare `item` against the current loadout. `current` is what equipping
 * would replace (null for empty slots / free mod slots / mutations below 6).
 */
export function compareItem(l: Loadout, item: ItemInstance, base: StatBlock, kitTags: readonly Tag[] = []): ItemComparison {
  const current = replacedBy(l, item);
  const autoEquip = canAutoEquip(l, item);

  const after = cloneLoadout(l);
  const sim = cloneItem(item);
  applyEquip(after, sim);

  const sBefore = computeStatsFor(l, base, kitTags);
  const sAfter = computeStatsFor(after, base, kitTags);

  const head: StatDelta[] = [];
  if (item.kind === 'weapon') {
    const ref: WeaponInstance | null =
      current && current.kind === 'weapon' ? current : (l.weapons[l.activeWeapon] ?? l.weapons.find((w) => !!w) ?? null);
    const simWeapon = after.weapons.find((w) => w?.uid === sim.uid) ?? (sim as WeaponInstance);
    head.push(dpsDelta(ref ? estimateDps(ref, sBefore) : null, estimateDps(simWeapon, sAfter)));
    if (current && current.kind === 'weapon') {
      if (current.modSlots !== simWeapon.modSlots) {
        head.push({
          label: 'Mod slots',
          value: `${current.modSlots} → ${simWeapon.modSlots}`,
          better: simWeapon.modSlots > current.modSlots,
        });
      }
      if (current.mods.length > 0) {
        // Mods stay on the replaced weapon (it drops with them attached).
        head.push({ label: 'Installed mods', value: `${current.mods.length} → ${simWeapon.mods.length}`, better: false });
      }
    }
  } else if (item.kind === 'mod') {
    const slot = modTargetSlot(l);
    const target = slot >= 0 ? l.weapons[slot] : null;
    const simTarget = slot >= 0 ? after.weapons[slot] : null;
    if (target && simTarget) {
      head.push(dpsDelta(estimateDps(target, sBefore), estimateDps(simTarget, sAfter)));
      head.push(...modifierDeltas(combineModifiers(target.mods), combineModifiers(simTarget.mods)));
    }
  }

  const statDeltas: StatDelta[] = [];
  for (const k of STAT_KEYS) {
    const d = statDelta(k, sBefore[k], sAfter[k]);
    if (d) statDeltas.push(d);
  }
  statDeltas.sort((a, b) => ORDER(a) - ORDER(b));

  const newTags = itemTags(item);
  const oldTags = current ? itemTags(current) : [];
  const synBefore = activeSynergiesFor(l, kitTags);
  const synAfter = activeSynergiesFor(after, kitTags);

  return {
    item,
    current,
    deltas: [...head, ...statDeltas],
    gainedTags: newTags.filter((t) => !oldTags.includes(t)),
    lostTags: oldTags.filter((t) => !newTags.includes(t)),
    synergiesGained: synAfter.filter((id) => !synBefore.includes(id)),
    synergiesLost: synBefore.filter((id) => !synAfter.includes(id)),
    autoEquip,
  };
}
